import type { IResource, ISchedule } from "@gettactic/api";
import { hasActiveResourceSchedule } from "@gettactic/helpers/src/schedules/hasActiveResourceSchedule";
import { format, parseISO } from "date-fns";
import {
  Ban,
  CalendarClock,
  CheckCircle2,
  CircleDashed,
  Footprints,
  type LucideIcon,
  ShieldCheck,
  User,
  UserCheck,
  Users
} from "lucide-react";

/**
 * The distinct states the on-click map resource card can surface in its status
 * band. Only states backed by real API data are represented here — the mockup's
 * anchor-days and permanent-desk states are intentionally omitted until the API
 * exposes per-day assignments / a permanent flag.
 */
export type ResourceStatusKind =
  | "available"
  | "reserved"
  | "partial"
  | "approval"
  | "unavailable"
  | "dedicated"
  | "released"
  | "assigned"
  | "assigned-multiple"
  | "walkup"
  | "walkup-occupied";

/** Visual tone mapped to token-based classes by {@link ResourceStatusBand}. */
export type ResourceStatusTone =
  | "green"
  | "blue"
  | "amber"
  | "neutral"
  | "indigo";

/** A second line under the band's label: "Normally dedicated to" plus the owner. */
export interface ResourceStatusDetail {
  text: string;
  user?: { id: string; name: string; profile_photo_url?: string | null };
}

export interface ResourceStatus {
  kind: ResourceStatusKind;
  label: string;
  tone: ResourceStatusTone;
  Icon: LucideIcon;
  detail?: ResourceStatusDetail;
}

/** The subset of resource fields the assignment helpers depend on. */
type ResourceAssignmentInput = Pick<
  IResource,
  "visible_assignment" | "user_visible_assignments" | "team_visible_assignments"
>;

/** The subset of resource fields the status derivation depends on. */
type ResourceStatusInput = ResourceAssignmentInput &
  Pick<
    IResource,
    | "is_open"
    | "is_available"
    | "is_approvable"
    | "is_dedicated"
    | "is_walkup"
    | "available_slots"
    | "reserved_slots"
  > &
  Partial<Pick<IResource, "dedicated_exception">> & { schedules?: ISchedule[] };

/** "Sep 25" from a `yyyy-MM-dd` day, for the released-desk labels. */
export const formatReleasedUntil = (day: string): string =>
  format(parseISO(`${day}T00:00:00`), "MMM d");

/**
 * Whether the dedicated desk is released to others on the day the resource
 * was looked at, while its dedicated user is away. Such a desk is booked like
 * any other, so the dedicated states do not apply to it.
 */
export const isResourceReleased = (
  resource: Pick<IResource, "is_dedicated"> &
    Partial<Pick<IResource, "dedicated_exception">>
): boolean => !!resource.is_dedicated && !!resource.dedicated_exception;

/** Number of people and teams the desk is assigned to (restricted-access list). */
export const assigneeCount = (resource: ResourceAssignmentInput): number =>
  (resource.user_visible_assignments?.length ?? 0) +
  (resource.team_visible_assignments?.length ?? 0);

/**
 * Whether the desk is actually assigned to an owner (a person or a team). This
 * is distinct from being *restricted* (`!is_open`, i.e. limited to some teams /
 * users): a restricted hot desk with no owner is still bookable by whoever is
 * allowed, so it must not read as "assigned".
 */
export const hasResourceAssignment = (
  resource: ResourceAssignmentInput
): boolean =>
  resource.visible_assignment != null || assigneeCount(resource) > 0;

/**
 * Fills in a resource's visible assignments from the office resources list.
 *
 * The map card's resource comes from the schedulable payload, which is fetched
 * without `assignments_date` and therefore carries no visible assignments,
 * while the map draws its assigned-user avatars from the office resources list
 * (fetched with that date). Reading the assignment from the same list keeps the
 * card and the map telling the same story — most visibly on a dedicated desk,
 * which shows its owner's avatar on the map but had nobody to name on the card.
 *
 * Purely additive: a resource that already carries an assignment is returned
 * untouched.
 */
export const withVisibleAssignments = <
  T extends ResourceAssignmentInput & Pick<IResource, "id">
>(
  resource: T,
  officeResources: readonly (ResourceAssignmentInput & Pick<IResource, "id">)[]
): T => {
  if (hasResourceAssignment(resource)) {
    return resource;
  }
  const source = officeResources.find(
    (x) => x.id === resource.id && hasResourceAssignment(x)
  );
  if (!source) {
    return resource;
  }
  return {
    ...resource,
    visible_assignment: source.visible_assignment,
    user_visible_assignments: source.user_visible_assignments,
    team_visible_assignments: source.team_visible_assignments
  };
};

/**
 * Whether an admin has switched the resource off (the "Unavailable" toggle on
 * the resource editor), as opposed to it merely not being bookable by the
 * current user for the queried window.
 *
 * Both read as `is_available: false` on the map's merged resource: the
 * schedulable payload lists a desk the viewer can't book (e.g. restricted to
 * other people) under `non_available`, and useOfficeResources zeroes
 * `is_available` for it. The office resources list keeps the raw toggle, so
 * it is the only place the two can be told apart.
 */
export const isResourceOutOfService = (
  resource: Pick<IResource, "id" | "is_available">,
  officeResources: readonly Pick<IResource, "id" | "is_available">[]
): boolean => {
  const source = officeResources.find((x) => x.id === resource.id);
  return !(source ?? resource).is_available;
};

/**
 * Whether the desk is restricted (open to only some teams/users) rather than an
 * open hot desk. Assignment implies restriction, so callers that already surface
 * an "assigned" band should not also show a "restricted" tag.
 */
export const isResourceRestricted = (resource: ResourceStatusInput): boolean =>
  // Dedicated implies !is_open, but it gets its own band — the extra
  // "Restricted" tag would be noise on top of it. A released desk shows its
  // release band instead, whoever it is released to.
  !resource.is_open && !resource.is_dedicated;

/**
 * Derives the status band shown under the header of the on-click resource card.
 *
 * Precedence matters — "can I book this?" wins over "whose is this?":
 *  - Not-bookable states come first, even for an assigned desk: out-of-service
 *    (`!is_available`) reads "UNAVAILABLE", and no capacity left today
 *    (`available_slots <= 0`) reads "RESERVED". Ownership is not lost — the
 *    assignee card renders separately.
 *  - Then an assigned desk (a real owner exists, still bookable) reads
 *    "ASSIGNED", before approval and partial availability, and finally the plain
 *    "available" state.
 *
 * Note: restriction (`!is_open`) is intentionally NOT a band on its own — a
 * restricted-but-unassigned desk keeps its real availability and the caller adds
 * a small "Restricted" tag (see {@link isResourceRestricted}).
 */
const unavailableStatus: ResourceStatus = {
  kind: "unavailable",
  label: "UNAVAILABLE",
  tone: "neutral",
  Icon: Ban
};

const reservedStatus: ResourceStatus = {
  kind: "reserved",
  label: "RESERVED",
  tone: "neutral",
  Icon: CalendarClock
};

export const getResourceStatus = (
  resource: ResourceStatusInput,
  /** True when office privacy hides who owns or occupies this resource. */
  hideIdentity = false
): ResourceStatus => {
  // A dedicated desk released for the day while its owner is away: bookable
  // by whoever the release names, so it reads as available, with until when
  // and a reminder that it is somebody's desk the rest of the time. Being
  // released does not make it bookable on its own, so the states that say it
  // cannot be taken still come first.
  if (isResourceReleased(resource) && resource.dedicated_exception) {
    if (!resource.is_available) {
      return unavailableStatus;
    }
    if (resource.available_slots <= 0) {
      return reservedStatus;
    }
    // On a released day the visible assignments name the release's grantees,
    // never the owner, so the owner is only the one the release itself names.
    const owner = hideIdentity
      ? undefined
      : resource.dedicated_exception.dedicated_user;
    return {
      kind: "released",
      label: `AVAILABLE UNTIL ${formatReleasedUntil(resource.dedicated_exception.ends_on).toUpperCase()}`,
      detail: owner
        ? { text: "Normally dedicated to", user: owner }
        : { text: "Normally a dedicated desk" },
      tone: "green",
      Icon: CalendarClock
    };
  }

  // A dedicated desk belongs to one person and is never bookable — that
  // identity is more informative than the generic "unavailable" it would
  // otherwise fall into.
  if (resource.is_dedicated) {
    return {
      kind: "dedicated",
      label: "DEDICATED DESK",
      tone: "indigo",
      Icon: UserCheck
    };
  }

  // Walk-up resources are never bookable in advance — their occupancy is
  // managed externally (check-in via API, not the reservation flow), so
  // "RESERVED"/"ASSIGNED"/"APPROVAL REQUIRED" would all be misleading here.
  // A walk-up only ever matters right now — is_available/available_slots
  // reflect the whole queried window (or are simply unreliable for this
  // resource type), so they must NOT factor in at all: an active (no-end)
  // schedule is the only real occupancy signal.
  if (resource.is_walkup) {
    if (hasActiveResourceSchedule(resource.schedules, true)) {
      return {
        kind: "walkup-occupied",
        label: "OCCUPIED",
        tone: "neutral",
        Icon: CheckCircle2
      };
    }
    return {
      kind: "walkup",
      label: "WALK-UP",
      tone: "blue",
      Icon: Footprints
    };
  }

  if (!resource.is_available) {
    return unavailableStatus;
  }

  // No capacity left for the selected date, but still a bookable desk — this is
  // "fully reserved today", not out-of-service. Overrides the assigned band: an
  // assigned desk that is fully booked can't be taken, so it reads "RESERVED".
  if (resource.available_slots <= 0) {
    return reservedStatus;
  }

  if (hasResourceAssignment(resource)) {
    const owners =
      assigneeCount(resource) || (resource.visible_assignment ? 1 : 0);
    if (owners > 1) {
      return {
        kind: "assigned-multiple",
        label: "ASSIGNED · MULTIPLE",
        tone: "indigo",
        Icon: Users
      };
    }
    return {
      kind: "assigned",
      label: "ASSIGNED DESK",
      tone: "indigo",
      Icon: User
    };
  }

  if (resource.is_approvable) {
    return {
      kind: "approval",
      label: "APPROVAL REQUIRED",
      tone: "amber",
      Icon: ShieldCheck
    };
  }

  if (resource.reserved_slots > 0 && resource.available_slots > 0) {
    return {
      kind: "partial",
      label: "PARTIALLY AVAILABLE",
      tone: "blue",
      Icon: CircleDashed
    };
  }

  return {
    kind: "available",
    label: "AVAILABLE",
    tone: "green",
    Icon: CheckCircle2
  };
};
